/**
 * /wireline-slickline/tool-strings/new — add a tool string
 * (POST /wireline-slickline/tool-strings, fieldiq-api#1336). The header only;
 * its items are added on the string's own page, which replaces this one.
 *
 * WL_GEAR_WRITE only (admin, office_staff, supervisor). A 409
 * duplicate_tool_string_name shows the API's sentence.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateWlToolString, useWlMeta } from '../../hooks/useWirelineSlickline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canWriteWlGear, wlRefusal } from '../../lib/wireline-slickline';
import { useBackToList } from '../downhole/recordPage';
import { ToolStringForm } from './ToolStringForm';
import { WL_EYEBROW, WlEmpty, WlLoading, WlPanel } from './WlBits';

export default function WlNewToolStringScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/tool-strings');
  const { user } = useAuth();
  const canAdd = canWriteWlGear(user?.role);
  const meta = useWlMeta();
  const create = useCreateWlToolString();
  const [error, setError] = useState<string | null>(null);
  const failure = queryLoadFailure(meta);

  return (
    <div data-testid="wl-new-tool-string-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WL_EYEBROW} title="New tool string" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlPanel eyebrow="Tool-string templates" title="Tool string">
            {!canAdd ? (
              <WlEmpty>Only an admin, office staff or a supervisor can add a tool string.</WlEmpty>
            ) : meta.isLoading ? (
              <WlLoading label="Loading form" />
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(meta)} kind={failure} subject="the tool-string form" />
            ) : (
              <ToolStringForm
                toolString={null}
                catalog={meta.data?.data ?? null}
                pending={create.isPending}
                error={error}
                submitLabel="Create tool string"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success('Tool string created');
                      if (res.data?.id) navigate(`/wireline-slickline/tool-strings/${res.data.id}`, { replace: true });
                      else navigate('/wireline-slickline/tool-strings', { replace: true });
                    },
                    onError: (err) => setError(wlRefusal(err, 'The tool string could not be created.')),
                  });
                }}
              />
            )}
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
